/**
 * Expert Rating Service
 * 
 * This service handles all API calls related to expert rating functionality.
 */

import { isMongoObjectIdString } from '../utils/mongoId';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Get authentication headers
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Create a new expert rating
 * @param {string} responseId - The AI message ID
 * @param {string} rating - 'thumbs_up' or 'thumbs_down'
 * @param {string} feedback - Plain text feedback (1-300 chars)
 * @returns {Promise<Object>} Created rating response
 */
export const createRating = async (responseId, rating, feedback) => {
  if (!isMongoObjectIdString(responseId)) {
    throw new Error('response_id must be the assistant message ObjectId from the chat API');
  }
  const response = await fetch(`${API_BASE}/ratings`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      response_id: responseId,
      rating: rating,
      feedback: feedback
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create rating');
  }

  return await response.json();
};

/**
 * Get aggregated ratings for a specific AI response
 * @param {string} responseId - The AI message ID
 * @returns {Promise<Object>} Rating metrics
 */
export const getResponseRatings = async (responseId) => {
  if (!isMongoObjectIdString(responseId)) {
    return null;
  }
  const response = await fetch(`${API_BASE}/ratings/response/${responseId}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null; // No ratings found
    }
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get ratings');
  }

  return await response.json();
};

/**
 * Get all ratings created by the current expert
 * @param {number} limit - Maximum number of ratings to return (default: 50)
 * @returns {Promise<Array>} List of expert's ratings
 */
export const getMyRatings = async (limit = 50) => {
  const response = await fetch(`${API_BASE}/ratings/my-ratings?limit=${limit}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get my ratings');
  }

  return await response.json();
};

/**
 * Get a specific rating by ID
 * @param {string} ratingId - The rating ID
 * @returns {Promise<Object>} Rating details
 */
export const getRating = async (ratingId) => {
  const response = await fetch(`${API_BASE}/ratings/${ratingId}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get rating');
  }

  return await response.json();
};

/**
 * Update an existing rating's feedback
 * @param {string} ratingId - The rating ID
 * @param {string} newFeedback - Updated feedback text
 * @returns {Promise<Object>} Updated rating response
 */
export const updateRating = async (ratingId, newFeedback) => {
  const response = await fetch(`${API_BASE}/ratings/${ratingId}?new_feedback=${encodeURIComponent(newFeedback)}`, {
    method: 'PUT',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update rating');
  }

  return await response.json();
};

/**
 * Delete a rating
 * @param {string} ratingId - The rating ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteRating = async (ratingId) => {
  const response = await fetch(`${API_BASE}/ratings/${ratingId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete rating');
  }

  return await response.json();
};

/**
 * Check if the current user has already rated a specific response
 * @param {string} responseId - The AI message ID
 * @returns {Promise<Object|null>} User's rating if exists, null otherwise
 */
export const checkUserRating = async (responseId) => {
  try {
    const myRatings = await getMyRatings(100); // Get more ratings to check
    return myRatings.find(rating => rating.response_id === responseId) || null;
  } catch (error) {
    console.error('Failed to check user rating:', error);
    return null;
  }
};

/**
 * Utility function to check if user is an expert
 * @param {Object} user - User object from AuthContext
 * @returns {boolean} True if user is an expert
 */
export const isExpert = (user) => {
  return user?.role === 'expert' || user?.is_expert === true || user?.role === 'superadmin';
};
